import React, { useEffect, useState } from 'react';
import {
  FileText,
  Plus,
  Search,
  Trash2,
  Edit3,
  Copy,
  Sparkles,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Layout,
  Smartphone,
  Monitor,
  Code,
  Eye,
  Download,
  Check,
  Share2,
  MessageSquare,
  Gift,
  MousePointerClick,
  Tag,
  Columns,
  Palette,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import { useAuth } from '../context/AuthContext';

interface TemplateItem {
  id: string;
  name: string;
  subject: string | null;
  htmlContent: string;
  textContent: string | null;
  category: string;
  tags: string[];
  updatedAt: string;
}

// ============================================================================
// BRAVE / BEEFREE STYLE PRE-MADE BLOCKS (1-Click Insert)
// ============================================================================

const VISUAL_BLOCKS = [
  {
    id: 'header',
    name: 'Header & Logo',
    icon: Layout,
    html: `<!-- HEADER BLOCK -->
<div style="background-color: #0f172a; padding: 24px 32px; border-bottom: 1px solid #1e293b; text-align: center;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr>
      <td align="left">
        <span style="font-size: 20px; font-weight: 800; color: #6366f1; letter-spacing: -0.5px;">keerav camp</span>
      </td>
      <td align="right">
        <a href="https://axiino.com" style="color: #94a3b8; font-size: 13px; text-decoration: none; margin-left: 16px;">Home</a>
        <a href="https://axiino.com/features" style="color: #94a3b8; font-size: 13px; text-decoration: none; margin-left: 16px;">Features</a>
      </td>
    </tr>
  </table>
</div>`,
  },
  {
    id: 'hero',
    name: 'Hero Headline & CTA',
    icon: Sparkles,
    html: `<!-- HERO BLOCK -->
<div style="background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%); padding: 48px 32px; text-align: center; border-radius: 12px; margin: 20px 0;">
  <span style="display: inline-block; background-color: rgba(99, 102, 241, 0.15); border: 1px solid rgba(99, 102, 241, 0.4); color: #818cf8; font-size: 11px; font-weight: 700; text-transform: uppercase; padding: 4px 12px; border-radius: 9999px; letter-spacing: 0.5px; margin-bottom: 16px;">
    Exclusive Announcement
  </span>
  <h1 style="color: #ffffff; font-size: 28px; font-weight: 800; line-height: 1.25; margin: 0 0 16px 0; letter-spacing: -0.5px;">
    Scale Your Outreach With Zero Spam Limits
  </h1>
  <p style="color: #cbd5e1; font-size: 16px; line-height: 1.6; max-width: 480px; margin: 0 auto 28px auto;">
    Hi {{firstName}}, upgrade {{company}} to dedicated sovereign mail nodes with automated warmups and 99.8% inbox placement.
  </p>
  <div>
    <a href="https://axiino.com/get-started" style="display: inline-block; background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); color: #ffffff; font-size: 14px; font-weight: 700; text-decoration: none; padding: 14px 32px; border-radius: 8px; box-shadow: 0 4px 14px rgba(79, 70, 229, 0.4);">
      Claim Your Free Trial &rarr;
    </a>
  </div>
</div>`,
  },
  {
    id: 'two-col',
    name: '2-Column Feature Cards',
    icon: Columns,
    html: `<!-- 2-COLUMN FEATURE GRID -->
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 24px 0;">
  <tr>
    <td width="48%" valign="top" style="background-color: #1e293b; padding: 24px; border-radius: 12px; border: 1px solid #334155;">
      <div style="width: 36px; height: 36px; background-color: rgba(99, 102, 241, 0.2); border-radius: 8px; margin-bottom: 16px; display: flex; align-items: center; justify-content: center; color: #818cf8; font-size: 18px; line-height: 36px; text-align: center;">⚡</div>
      <h3 style="color: #ffffff; font-size: 16px; font-weight: 700; margin: 0 0 8px 0;">Sovereign Sending Pool</h3>
      <p style="color: #94a3b8; font-size: 13px; line-height: 1.5; margin: 0;">Dedicated IPs that eliminate shared SaaS reputation drops and blacklist risks.</p>
    </td>
    <td width="4%"></td>
    <td width="48%" valign="top" style="background-color: #1e293b; padding: 24px; border-radius: 12px; border: 1px solid #334155;">
      <div style="width: 36px; height: 36px; background-color: rgba(16, 185, 129, 0.2); border-radius: 8px; margin-bottom: 16px; display: flex; align-items: center; justify-content: center; color: #34d399; font-size: 18px; line-height: 36px; text-align: center;">🛡️</div>
      <h3 style="color: #ffffff; font-size: 16px; font-weight: 700; margin: 0 0 8px 0;">Automated DNS Warmup</h3>
      <p style="color: #94a3b8; font-size: 13px; line-height: 1.5; margin: 0;">AI ramp schedules ramp your volume safely to 50,000+ mails per day.</p>
    </td>
  </tr>
</table>`,
  },
  {
    id: 'cta-button',
    name: 'Call to Action Button',
    icon: MousePointerClick,
    html: `<!-- CTA BUTTON BLOCK -->
<div style="text-align: center; margin: 32px 0;">
  <a href="https://axiino.com/demo" style="display: inline-block; background-color: #6366f1; color: #ffffff; font-size: 15px; font-weight: 700; text-decoration: none; padding: 14px 36px; border-radius: 8px; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35);">
    Book a Live 1-on-1 Demo
  </a>
  <p style="color: #64748b; font-size: 12px; margin: 8px 0 0 0;">No credit card required &bull; Takes 2 minutes</p>
</div>`,
  },
  {
    id: 'testimonial',
    name: 'Testimonial & Social Proof',
    icon: MessageSquare,
    html: `<!-- TESTIMONIAL BLOCK -->
<div style="background-color: #0f172a; border-left: 4px solid #6366f1; padding: 20px 24px; border-radius: 8px; margin: 24px 0; border: 1px solid #1e293b;">
  <div style="color: #f59e0b; font-size: 14px; margin-bottom: 8px;">★★★★★</div>
  <p style="color: #e2e8f0; font-size: 14px; font-style: italic; line-height: 1.6; margin: 0 0 12px 0;">
    &ldquo;We moved our 100k daily campaign volume to keerav camp and deliverability jumped from 76% to 99.4% in 3 weeks.&rdquo;
  </p>
  <p style="color: #94a3b8; font-size: 12px; margin: 0;">
    <strong style="color: #ffffff;">Marcus Vance</strong> &bull; Head of Growth, SaaSify Inc.
  </p>
</div>`,
  },
  {
    id: 'promo-box',
    name: 'Special Offer / Promo Coupon',
    icon: Gift,
    html: `<!-- PROMO OFFER BOX -->
<div style="border: 2px dashed #6366f1; background-color: rgba(99, 102, 241, 0.08); padding: 24px; border-radius: 12px; text-align: center; margin: 24px 0;">
  <span style="font-size: 12px; font-weight: 700; color: #818cf8; text-transform: uppercase;">Limited Time Deal for {{company}}</span>
  <h2 style="color: #ffffff; font-size: 22px; font-weight: 800; margin: 8px 0 12px 0;">Get 40% Off Your First 3 Months</h2>
  <div style="display: inline-block; background-color: #1e1b4b; border: 1px solid #6366f1; padding: 8px 20px; border-radius: 6px; font-family: monospace; font-size: 16px; color: #a5b4fc; font-weight: 700; margin-bottom: 12px;">
    CODE: SOVEREIGN40
  </div>
  <p style="color: #94a3b8; font-size: 12px; margin: 0;">Valid until the end of this month &bull; Applies to all tiers</p>
</div>`,
  },
  {
    id: 'socials',
    name: 'Social Media Icons',
    icon: Share2,
    html: `<!-- SOCIAL ICONS BAR -->
<div style="text-align: center; padding: 20px 0; border-top: 1px solid #1e293b; margin-top: 32px;">
  <p style="color: #64748b; font-size: 12px; margin-bottom: 12px;">Follow our engineering updates:</p>
  <a href="https://twitter.com" style="display: inline-block; margin: 0 8px; color: #818cf8; text-decoration: none; font-size: 12px; font-weight: 600;">Twitter (X)</a> &bull;
  <a href="https://linkedin.com" style="display: inline-block; margin: 0 8px; color: #818cf8; text-decoration: none; font-size: 12px; font-weight: 600;">LinkedIn</a> &bull;
  <a href="https://github.com" style="display: inline-block; margin: 0 8px; color: #818cf8; text-decoration: none; font-size: 12px; font-weight: 600;">GitHub</a>
</div>`,
  },
  {
    id: 'footer',
    name: 'Compliance Footer & Unsubscribe',
    icon: Tag,
    html: `<!-- FOOTER BLOCK -->
<div style="text-align: center; padding: 24px 0 12px 0; border-top: 1px solid #1e293b; color: #64748b; font-size: 11px; line-height: 1.6;">
  <p style="margin: 0 0 8px 0;">You received this email because you opted into updates from {{company}} or connected on outreach.</p>
  <p style="margin: 0 0 8px 0;">Axiino Technologies &bull; Sovereign Mail Infrastructure Node</p>
  <p style="margin: 0;">
    <a href="{{unsubscribeUrl}}" style="color: #818cf8; text-decoration: underline;">Unsubscribe from this campaign</a> &bull;
    <a href="https://axiino.com/privacy" style="color: #64748b; text-decoration: none; margin-left: 8px;">Privacy Policy</a>
  </p>
</div>`,
  },
];

// ============================================================================
// FULL DESIGN PRESETS (Brave / BeeFree Style)
// ============================================================================

const DESIGN_PRESETS = [
  {
    name: 'Sovereign Dark Luxury',
    category: 'Product Launch',
    subject: 'Welcome to {{company}} — Your Sovereign Email Node is Ready',
    tags: ['Luxury', 'Dark Mode'],
    html: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 32px 24px; background-color: #0f172a; color: #f8fafc; border-radius: 16px; border: 1px solid #1e293b;">
  <div style="border-bottom: 1px solid #334155; padding-bottom: 20px; margin-bottom: 24px;">
    <h1 style="color: #6366f1; font-size: 24px; margin: 0; font-weight: 800;">keerav camp</h1>
    <p style="color: #94a3b8; font-size: 13px; margin-top: 4px;">Sovereign High-Throughput Delivery</p>
  </div>
  <p style="font-size: 16px; line-height: 24px;">Hi {{firstName}},</p>
  <p style="font-size: 15px; line-height: 24px; color: #cbd5e1;">Welcome to your private Stalwart email infrastructure. Your dedicated node is now actively routing outbound and inbound traffic with zero 3rd-party SaaS lock-in.</p>
  <div style="background-color: #1e293b; border-left: 4px solid #6366f1; padding: 16px; border-radius: 8px; margin: 24px 0;">
    <p style="margin: 0; font-size: 14px; color: #e2e8f0; font-weight: 600;">Account Details:</p>
    <p style="margin: 4px 0 0; font-size: 13px; color: #94a3b8;">Recipient: {{email}} | Company: {{company}}</p>
  </div>
  <a href="https://app-staging.axiino.com/webmail" style="display: inline-block; background-color: #4f46e5; color: #ffffff; padding: 12px 24px; border-radius: 10px; text-decoration: none; font-weight: 600; font-size: 14px;">Open Sovereign Webmail</a>
  <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #334155; font-size: 12px; color: #64748b;">
    <p style="margin: 0;">You received this email because you registered on {{company}}.<br><a href="{{unsubscribeUrl}}" style="color: #818cf8; text-decoration: underline;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    name: 'B2B Executive Cold Outreach',
    category: 'Cold Outreach',
    subject: 'Quick question regarding sovereign email at {{company}}',
    tags: ['B2B', 'Sales'],
    html: `<div style="font-family: Arial, sans-serif; max-width: 580px; margin: 0 auto; color: #1e293b; line-height: 1.6; font-size: 15px; background-color: #ffffff; padding: 32px; border-radius: 12px; border: 1px solid #e2e8f0;">
  <p>Hi {{firstName}},</p>
  <p>I noticed {{company}} is scaling outreach and wanted to see how you're managing deliverability and mailbox reputation right now.</p>
  <p>We recently helped teams migrate from third-party SMTP limits to an in-house sovereign cluster—achieving 99.4% inbox placement without paying per-seat SaaS costs.</p>
  <p>Do you have 5 minutes this Thursday to see how it works?</p>
  <p style="margin-top: 24px;">Best regards,<br><strong>Outreach Specialist</strong><br>Sovereign Email Team</p>
  <p style="font-size: 11px; color: #94a3b8; margin-top: 32px; border-top: 1px solid #f1f5f9; padding-top: 16px;"><a href="{{unsubscribeUrl}}" style="color: #94a3b8; text-decoration: underline;">Opt out of future communications</a></p>
</div>`,
  },
  {
    name: 'E-Commerce Flash Sale & Promo',
    category: 'Marketing',
    subject: 'Exclusive: 40% Off for {{company}} Members (48 Hours Only)',
    tags: ['Retail', 'Flash Sale'],
    html: `<div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; background-color: #111827; color: #f9fafb; border-radius: 16px; overflow: hidden; border: 1px solid #374151;">
  <div style="background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%); padding: 36px 24px; text-align: center;">
    <span style="background-color: rgba(0,0,0,0.3); color: #ffffff; padding: 4px 12px; border-radius: 9999px; font-size: 11px; font-weight: 700; text-transform: uppercase;">48-Hour Private Sale</span>
    <h1 style="color: #ffffff; font-size: 32px; font-weight: 900; margin: 12px 0 8px 0;">40% OFF STOREWIDE</h1>
    <p style="color: #fdf2f8; font-size: 14px; margin: 0;">Special VIP invitation for {{firstName}} from {{company}}</p>
  </div>
  <div style="padding: 32px 24px; text-align: center;">
    <p style="font-size: 15px; color: #d1d5db; line-height: 1.6; margin-bottom: 24px;">Use your personal code at checkout to unlock your discount across all dedicated mailboxes and infrastructure nodes.</p>
    <div style="background-color: #1f2937; border: 2px dashed #ec4899; padding: 12px 24px; display: inline-block; border-radius: 8px; font-size: 18px; font-weight: 800; font-family: monospace; color: #f472b6; margin-bottom: 24px;">
      VIP40-{{firstName}}
    </div>
    <div>
      <a href="https://axiino.com/shop" style="display: inline-block; background-color: #ec4899; color: #ffffff; padding: 14px 36px; border-radius: 8px; font-weight: 700; font-size: 15px; text-decoration: none;">Shop The Flash Sale &rarr;</a>
    </div>
  </div>
  <div style="background-color: #0b0f19; padding: 20px; text-align: center; font-size: 11px; color: #6b7280; border-top: 1px solid #1f2937;">
    <p style="margin: 0;">Offer expires in 48 hours. <a href="{{unsubscribeUrl}}" style="color: #9ca3af; text-decoration: underline;">Unsubscribe</a></p>
  </div>
</div>`,
  },
];

export const TemplatesPage: React.FC = () => {
  const { currentOrg } = useAuth();
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editor Modal State
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState<string>('');
  const [formSubject, setFormSubject] = useState<string>('');
  const [formCategory, setFormCategory] = useState<string>('Marketing');
  const [formTags, setFormTags] = useState<string>('');
  const [formHtml, setFormHtml] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<'both' | 'edit' | 'preview'>('both');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [copiedHtml, setCopiedHtml] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getTemplates();
      setTemplates(data || []);
    } catch (err: any) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [currentOrg?.id]);

  const handleOpenCreate = (preset?: typeof DESIGN_PRESETS[0]) => {
    setEditingId(null);
    setFormName(preset ? `${preset.name} (Copy)` : '');
    setFormSubject(preset ? preset.subject : '');
    setFormCategory(preset ? preset.category : 'Marketing');
    setFormTags(preset ? preset.tags.join(', ') : 'Newsletter');
    setFormHtml(preset ? preset.html : `<div style="font-family: -apple-system, sans-serif; max-width: 600px; margin: 0 auto; padding: 32px 24px; background-color: #0f172a; color: #f8fafc; border-radius: 12px;">\n  <h2 style="color: #6366f1; margin-top: 0;">Hello {{firstName}},</h2>\n  <p style="color: #cbd5e1; line-height: 1.6;">Welcome to our latest campaign. Click the block tools on the left to add high-converting sections.</p>\n  <div style="margin-top: 24px;">\n    <a href="https://axiino.com" style="background-color: #4f46e5; color: #ffffff; padding: 10px 20px; border-radius: 6px; text-decoration: none; font-weight: 600;">Explore Now</a>\n  </div>\n</div>`);
    setShowModal(true);
  };

  const handleOpenEdit = (t: TemplateItem) => {
    setEditingId(t.id);
    setFormName(t.name);
    setFormSubject(t.subject || '');
    setFormCategory(t.category);
    setFormTags((t.tags || []).join(', '));
    setFormHtml(t.htmlContent);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const tagsArray = formTags.split(',').map((t) => t.trim()).filter(Boolean);
      if (editingId) {
        await apiClient.updateTemplate(editingId, {
          name: formName,
          subject: formSubject,
          category: formCategory,
          tags: tagsArray,
          htmlContent: formHtml,
        });
        setMessage({ type: 'success', text: 'Template updated successfully!' });
      } else {
        await apiClient.createTemplate({
          name: formName,
          subject: formSubject,
          category: formCategory,
          tags: tagsArray,
          htmlContent: formHtml,
        });
        setMessage({ type: 'success', text: 'Template created successfully!' });
      }
      setShowModal(false);
      await fetchTemplates();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await apiClient.deleteTemplate(id);
      setMessage({ type: 'success', text: `Template "${name}" deleted` });
      await fetchTemplates();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete template' });
    }
  };

  const insertBlock = (blockHtml: string) => {
    setFormHtml((prev) => `${prev.trim()}\n\n${blockHtml}`);
  };

  const insertVariable = (varName: string) => {
    setFormHtml((prev) => `${prev} ${varName}`);
  };

  const copyTemplateHtml = () => {
    navigator.clipboard.writeText(formHtml);
    setCopiedHtml(true);
    setTimeout(() => setCopiedHtml(false), 2000);
  };

  const downloadHtmlFile = () => {
    const blob = new Blob([formHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(formName || 'email-template').toLowerCase().replace(/\s+/g, '-')}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.subject || '').toLowerCase().includes(search.toLowerCase());
    const matchesCat = selectedCategory === 'ALL' || t.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2.5">
            <FileText className="w-6 h-6 text-brand-400" />
            <span>Email Templates Studio (Brave / BeeFree Visual Builder)</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Build high-converting responsive HTML email templates with visual drag-and-drop components, personalization tokens, and live device preview.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchTemplates}
            disabled={loading}
            className="p-2.5 rounded-xl border border-slate-700 bg-slate-900/80 hover:bg-slate-800 text-slate-300 transition"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => handleOpenCreate()}
            className="px-4 py-2.5 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white shadow-lg shadow-brand-950/40 transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Blank Template
          </button>
        </div>
      </div>

      {/* Status Alert */}
      {message && (
        <div
          className={`p-3.5 rounded-xl text-xs flex items-center justify-between border ${
            message.type === 'success'
              ? 'bg-emerald-950/40 border-emerald-800/80 text-emerald-300'
              : 'bg-rose-950/40 border-rose-800/80 text-rose-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            ) : (
              <XCircle className="w-4 h-4 text-rose-400" />
            )}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Starter Templates Banner */}
      <div className="bg-gradient-to-r from-brand-950/50 via-slate-900/40 to-purple-950/30 border border-brand-800/40 p-5 rounded-2xl shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs font-bold text-brand-300 uppercase tracking-wider">
            <Sparkles className="w-4 h-4 text-brand-400" />
            <span>Pre-Engineered High-Converting Design Presets</span>
          </div>
          <span className="text-[11px] text-slate-400 hidden sm:inline">Click &ldquo;Use Template&rdquo; to launch in Visual Builder</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
          {DESIGN_PRESETS.map((starter, idx) => (
            <div
              key={idx}
              className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl flex flex-col justify-between hover:border-brand-500/60 transition group hover:shadow-lg hover:shadow-brand-500/5"
            >
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-brand-950 text-brand-300 border border-brand-800">
                    {starter.category}
                  </span>
                  <span className="text-[10px] text-slate-500">{starter.tags[0]}</span>
                </div>
                <h4 className="text-xs font-bold text-white group-hover:text-brand-300 transition">
                  {starter.name}
                </h4>
                <p className="text-[11px] text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                  {starter.subject}
                </p>
              </div>

              <div className="mt-3 pt-2.5 border-t border-slate-800/80 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => handleOpenCreate(starter)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-600/20 hover:bg-brand-600 text-brand-300 hover:text-white transition w-full text-center flex items-center justify-center gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" />
                  <span>Use This Preset</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search templates by name or subject line..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 placeholder-slate-500"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          {['ALL', 'Marketing', 'Onboarding', 'Cold Outreach', 'Newsletter', 'Transactional'].map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                selectedCategory === cat
                  ? 'bg-brand-600 text-white shadow-md shadow-brand-950/40'
                  : 'bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="p-16 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-brand-400" />
          <span className="text-xs">Loading email templates...</span>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="p-16 text-center text-slate-400 border border-dashed border-slate-800 rounded-2xl space-y-3 bg-slate-900/20">
          <FileText className="w-10 h-10 text-slate-600 mx-auto" />
          <h3 className="text-sm font-bold text-white">No custom templates found</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            Get started by picking one of our pre-made presets above or click create blank template.
          </p>
          <button
            onClick={() => handleOpenCreate()}
            className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create First Template</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((t) => (
            <div
              key={t.id}
              className="p-4 bg-slate-900/50 border border-slate-800 rounded-2xl flex flex-col justify-between hover:border-slate-700 transition group shadow-md"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-800 text-brand-300 border border-slate-700">
                    {t.category}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    Updated {new Date(t.updatedAt).toLocaleDateString()}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-white group-hover:text-brand-300 transition line-clamp-1">
                  {t.name}
                </h3>
                <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                  {t.subject || 'No subject line specified'}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {(t.tags || []).slice(0, 2).map((tg, i) => (
                    <span key={i} className="text-[10px] font-mono text-slate-500">
                      #{tg}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleOpenEdit(t)}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-brand-600 text-slate-300 hover:text-white transition"
                    title="Edit in Visual Studio"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(t.id, t.name)}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-900 text-slate-300 hover:text-rose-300 transition"
                    title="Delete Template"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===================================================================== */}
      {/* FULL VISUAL BUILDER MODAL (Brave / BeeFree Style) */}
      {/* ===================================================================== */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-2 sm:p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-6xl max-h-[95vh] flex flex-col shadow-2xl overflow-hidden my-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-800 bg-slate-950/80 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-brand-600/20 border border-brand-500/30 flex items-center justify-center text-brand-400">
                  <Palette className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-white flex items-center gap-2">
                    {editingId ? 'Visual Template Editor' : 'Author New Visual Template'}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-950 text-brand-300 font-normal border border-brand-800/80 hidden sm:inline">
                      Brave-Style Visual Studio
                    </span>
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Click any component block on the left to instantly insert high-converting HTML blocks into your canvas.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={copyTemplateHtml}
                  className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition"
                  title="Copy HTML Source"
                >
                  {copiedHtml ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">{copiedHtml ? 'Copied!' : 'Copy Code'}</span>
                </button>
                <button
                  type="button"
                  onClick={downloadHtmlFile}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  title="Export .html file"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white p-1">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSave} className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
                {/* Meta details bar */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Template Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Q4 Executive Cold Sequence"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Category</label>
                    <select
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                    >
                      <option value="Marketing">Marketing</option>
                      <option value="Onboarding">Onboarding</option>
                      <option value="Cold Outreach">Cold Outreach</option>
                      <option value="Newsletter">Newsletter</option>
                      <option value="Transactional">Transactional</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Tags (Comma-separated)</label>
                    <input
                      type="text"
                      placeholder="B2B, SaaS, Q4"
                      value={formTags}
                      onChange={(e) => setFormTags(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Default Subject Line</label>
                  <input
                    type="text"
                    placeholder="e.g. Hi {{firstName}}, question about scaling {{company}} deliverability"
                    value={formSubject}
                    onChange={(e) => setFormSubject(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 font-mono"
                  />
                </div>

                {/* Personalization Variable Injectors */}
                <div className="p-3 bg-slate-950/70 rounded-xl border border-slate-800 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-400 mr-2 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-brand-400" /> Insert Variable:
                  </span>
                  {[
                    { tag: '{{firstName}}', desc: 'Recipient Name' },
                    { tag: '{{lastName}}', desc: 'Last Name' },
                    { tag: '{{company}}', desc: 'Company' },
                    { tag: '{{email}}', desc: 'Work Email' },
                    { tag: '{{unsubscribeUrl}}', desc: 'Opt-Out Link' },
                  ].map((v) => (
                    <button
                      key={v.tag}
                      type="button"
                      onClick={() => insertVariable(v.tag)}
                      className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-brand-600 text-brand-300 hover:text-white border border-slate-800 text-xs font-mono font-semibold transition"
                      title={v.desc}
                    >
                      {v.tag}
                    </button>
                  ))}
                </div>

                {/* Studio Canvas: Visual Blocks Palette (Left) + Split Editor/Live Preview (Right) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  {/* Left Column: Visual Blocks Palette */}
                  <div className="lg:col-span-3 bg-slate-950/80 border border-slate-800 rounded-xl p-3 space-y-2.5">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-800 text-xs font-bold text-white">
                      <span className="flex items-center gap-1.5">
                        <Layout className="w-3.5 h-3.5 text-brand-400" />
                        Component Blocks
                      </span>
                      <span className="text-[10px] text-slate-500 font-normal">Click to insert</span>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-1 gap-1.5 max-h-[460px] overflow-y-auto pr-1">
                      {VISUAL_BLOCKS.map((blk) => {
                        const Icon = blk.icon;
                        return (
                          <button
                            key={blk.id}
                            type="button"
                            onClick={() => insertBlock(blk.html)}
                            className="p-2.5 rounded-xl border border-slate-800/80 bg-slate-900/60 hover:bg-brand-950/40 hover:border-brand-500/60 text-left transition flex items-center gap-2.5 group"
                          >
                            <div className="w-7 h-7 rounded-lg bg-slate-800 group-hover:bg-brand-600/20 text-slate-400 group-hover:text-brand-300 flex items-center justify-center shrink-0 transition">
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold text-white group-hover:text-brand-300 truncate">
                                {blk.name}
                              </div>
                              <div className="text-[10px] text-slate-500 truncate">+ Insert Section</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Column: Code Editor & Live Preview */}
                  <div className="lg:col-span-9 flex flex-col space-y-2.5">
                    {/* View Controls Toolbar */}
                    <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-slate-950 rounded-xl border border-slate-800">
                      {/* Split / Code / Preview Modes */}
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setPreviewMode('both')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${
                            previewMode === 'both' ? 'bg-brand-600 text-white' : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          <Columns className="w-3.5 h-3.5" />
                          <span>Split View</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewMode('edit')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${
                            previewMode === 'edit' ? 'bg-brand-600 text-white' : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          <Code className="w-3.5 h-3.5" />
                          <span>HTML Code</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewMode('preview')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${
                            previewMode === 'preview' ? 'bg-brand-600 text-white' : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>Live Canvas</span>
                        </button>
                      </div>

                      {/* Device Simulation: Desktop vs Mobile Preview */}
                      <div className="flex items-center gap-1 bg-slate-900 p-0.5 rounded-lg border border-slate-800">
                        <button
                          type="button"
                          onClick={() => setPreviewDevice('desktop')}
                          className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 transition ${
                            previewDevice === 'desktop' ? 'bg-brand-600 text-white' : 'text-slate-400 hover:text-white'
                          }`}
                          title="Desktop View (Full Width)"
                        >
                          <Monitor className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Desktop</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewDevice('mobile')}
                          className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 transition ${
                            previewDevice === 'mobile' ? 'bg-brand-600 text-white' : 'text-slate-400 hover:text-white'
                          }`}
                          title="Mobile Smartphone View (375px Screen)"
                        >
                          <Smartphone className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Mobile (375px)</span>
                        </button>
                      </div>
                    </div>

                    {/* Canvas Panes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 h-[460px]">
                      {(previewMode === 'edit' || previewMode === 'both') && (
                        <div className={`h-full flex flex-col ${previewMode === 'edit' ? 'col-span-2' : ''}`}>
                          <textarea
                            required
                            value={formHtml}
                            onChange={(e) => setFormHtml(e.target.value)}
                            placeholder="Enter HTML template markup or click blocks on the left..."
                            className="w-full h-full p-3.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-200 font-mono focus:outline-none focus:border-brand-500 resize-none leading-relaxed"
                          />
                        </div>
                      )}

                      {(previewMode === 'preview' || previewMode === 'both') && (
                        <div
                          className={`h-full rounded-xl overflow-y-auto p-4 border border-slate-800 flex justify-center bg-slate-950/60 ${
                            previewMode === 'preview' ? 'col-span-2' : ''
                          }`}
                        >
                          <div
                            className={`transition-all bg-white text-slate-900 rounded-xl overflow-hidden shadow-2xl ${
                              previewDevice === 'mobile' ? 'w-[375px] max-w-full p-2 border-4 border-slate-700' : 'w-full p-4'
                            }`}
                          >
                            {previewDevice === 'mobile' && (
                              <div className="bg-slate-900 text-white text-[10px] px-3 py-1 rounded-t-lg mb-2 flex items-center justify-between font-mono">
                                <span>9:41 AM</span>
                                <span>100% 🔋</span>
                              </div>
                            )}
                            <div
                              dangerouslySetInnerHTML={{
                                __html: formHtml
                                  .replace(/{{firstName}}/g, 'Alex')
                                  .replace(/{{lastName}}/g, 'Morgan')
                                  .replace(/{{company}}/g, 'Acme Corp')
                                  .replace(/{{email}}/g, 'alex.morgan@acme.test')
                                  .replace(/{{unsubscribeUrl}}/g, '#'),
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Footer */}
              <div className="p-4 sm:p-5 border-t border-slate-800 flex items-center justify-between bg-slate-900/95 shrink-0 sticky bottom-0">
                <span className="text-xs text-slate-400 hidden sm:inline">
                  Template renders 100% responsive across Gmail, Apple Mail, Outlook &amp; mobile clients.
                </span>
                <div className="flex items-center gap-3 ml-auto">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2 shadow-lg shadow-brand-950/40"
                  >
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {editingId ? 'Save Changes' : 'Create & Save Template'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
